import { useState } from 'react';
import { useAccountsCrumb } from './lib/usePhoneCrumb';
import { toast } from 'sonner';
import { Button, Card, DChip, DataTable, Pane, PaneHead } from '@/components/kit';
import type { TableRow } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { useNarrowViewport, useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PeriodRow } from './components/PeriodRow';
import { exportRows } from './lib/exportCsv';
import { exportPdf } from './lib/exportPdf';
import { exportXlsx } from './lib/exportXlsx';
import { useTenantStore } from '@/stores/useTenantStore';
import { periodLabel,
  dayLabel,
  monthName,
  thisPeriod,
  useAccountsSummary,
  useExportLog,
  useLogExport,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';

/**
 * Export centre — `g_export` (13). THE MONTHLY CLOSE PACKAGE.
 *
 * EVERY EXPORT IS LOGGED — the screen's own closing line, so the log entry is
 * written as part of producing the file rather than beside it, and the pane
 * reads it straight back. "Who pulled what, when" is a promise a screen makes
 * about itself, and a promise like that has to be true or the screen is lying.
 *
 * THREE FORMATS, ALL REAL. .xlsx is built in the browser (one sheet per table
 * for the close package), .csv is the same rows as plain text, and the branded
 * PDF is the same table on the clinic's letterhead.
 */

interface Table {
  key: string;
  label: string;
  /** Where its rows come from, and what the file will hold. */
  source: string;
  fetch: (period: string) => Promise<{ headers: string[]; rows: string[][] }>;
}

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;
const list = <T,>(body: unknown): T[] => {
  const d = unwrap<T[] | { data?: T[] }>(body);
  return Array.isArray(d) ? d : ((d as { data?: T[] })?.data ?? []);
};
const cash = (v: unknown) => (Number(v ?? 0)).toFixed(2);
const minor = (v: unknown) => (Number(v ?? 0) / 100).toFixed(2);

/** The month's bills — paged, because the list endpoint refuses more than a hundred. */
async function billsOf(period: string): Promise<Array<Record<string, unknown>>> {
  const out: Array<Record<string, unknown>> = [];
  for (let page = 1; page <= 12; page += 1) {
    const res = await apiClient.get('/billing', { params: { limit: 100, page } });
    const batch = list<Record<string, unknown>>(res.data);
    out.push(...batch);
    if (batch.length < 100) break;
  }
  return out.filter((b) => String(b.date ?? '').startsWith(period));
}

const TABLES: Table[] = [
  {
    key: 'bills',
    label: 'Bills — all lines & statuses',
    source: 'every invoice raised in the period, whatever it is owed',
    fetch: async (period) => {
      const rows = await billsOf(period);
      return {
        headers: ['Bill', 'Date', 'Total', 'Tax', 'Paid', 'Owing', 'Status'],
        rows: rows.map((b) => [
          String(b.invoiceNumber ?? ''),
          String(b.date ?? ''),
          cash(b.total),
          cash(b.taxAmount),
          cash(b.amountPaid),
          cash(b.balance),
          String(b.status ?? ''),
        ]),
      };
    },
  },
  {
    key: 'payments',
    label: 'Payments — by method',
    source: 'what was collected against each bill, and how it was taken',
    fetch: async (period) => {
      const rows = (await billsOf(period)).filter((b) => Number(b.amountPaid ?? 0) > 0);
      return {
        headers: ['Bill', 'Date', 'Method', 'Collected', 'Owing'],
        rows: rows.map((b) => [
          String(b.invoiceNumber ?? ''),
          String(b.date ?? ''),
          String(b.paymentMethod ?? ''),
          cash(b.amountPaid),
          cash(b.balance),
        ]),
      };
    },
  },
  {
    key: 'voids',
    label: 'Refunds, voids & discounts — with reasons',
    source: 'every bill that was cancelled or refunded, and why',
    fetch: async (period) => {
      const res = await apiClient.get('/accounts/export/refunds', { params: { period } });
      const rows = list<Record<string, unknown>>(res.data);
      return {
        headers: ['Date', 'Bill', 'Reason', 'Note', 'Amount', 'By'],
        rows: rows.map((r) => [
          String(r.date ?? ''),
          String(r.bill ?? ''),
          String(r.reason ?? ''),
          String(r.note ?? ''),
          minor(r.amountMinor),
          String(r.by ?? ''),
        ]),
      };
    },
  },
  {
    key: 'claims',
    label: 'Claims & remittances — per insurer',
    source: 'every claim and what has been remitted against it',
    fetch: async () => {
      const res = await apiClient.get('/billing/claims', { params: { limit: 100 } });
      const rows = list<Record<string, unknown>>(res.data);
      return {
        headers: ['Claim', 'Insurer', 'Status', 'Billed', 'Outstanding', 'Date'],
        rows: rows.map((c) => [
          String(c.invoiceNumber ?? ''),
          String(c.payerLabel ?? ''),
          String(c.status ?? ''),
          cash(c.total),
          cash(c.balance),
          String(c.date ?? ''),
        ]),
      };
    },
  },
  {
    key: 'expenses',
    label: 'Expenses, payables & payroll',
    source: 'the spend side — every category, with what has no paper behind it',
    fetch: async (period) => {
      const res = await apiClient.get('/expenses/lines', { params: { period } });
      const rows = list<Record<string, unknown>>(res.data);
      return {
        headers: ['Date', 'Category', 'What for', 'Amount', 'Supplier', 'Document', 'Due', 'Paid'],
        rows: rows.map((e) => [
          String(e.spentOn ?? ''),
          String(e.category ?? ''),
          String(e.description ?? ''),
          minor(e.amountMinor),
          String(e.supplierName ?? ''),
          String(e.documentName ?? ''),
          String(e.dueOn ?? ''),
          String(e.paidOn ?? ''),
        ]),
      };
    },
  },
  {
    key: 'payroll',
    label: 'The payout run — every person and deal',
    source: "the month's run, frozen if it has been sent",
    fetch: async (period) => {
      const res = await apiClient.get('/payroll', { params: { period } });
      const run = unwrap<{ lines?: Array<Record<string, unknown>> }>(res.data);
      return {
        headers: ['Lane', 'Person', 'Deal', 'Gross', 'Deductions', 'Net', 'Status'],
        rows: (run?.lines ?? []).map((l) => [
          String(l.lane ?? ''),
          String(l.personName ?? ''),
          String(l.dealSummary ?? ''),
          minor(l.grossMinor),
          minor(l.deductionsMinor),
          minor(l.netMinor),
          String(l.status ?? ''),
        ]),
      };
    },
  },
  {
    key: 'tax',
    label: 'The tax split — exempt against taxable',
    source: 'what the filing is built from',
    fetch: async (period) => {
      const res = await apiClient.get('/accounts/tax', { params: { period } });
      const tax = unwrap<{ classes?: Array<Record<string, unknown>> }>(res.data);
      return {
        headers: ['Tax class', 'Rate', 'Base', 'VAT', 'Bills'],
        rows: (tax?.classes ?? []).map((c) => [
          String(c.label ?? ''),
          `${c.ratePercent ?? 0}%`,
          minor(c.baseMinor),
          minor(c.vatMinor),
          String(c.bills ?? 0),
        ]),
      };
    },
  },
  {
    key: 'receivables',
    label: 'Receivables — who owes, and how long',
    source: 'the ageing, whatever month it was billed in',
    fetch: async () => {
      const res = await apiClient.get('/accounts/receivables');
      const rows = list<Record<string, unknown>>(res.data);
      return {
        headers: ['Who', 'Kind', 'Owed', 'Oldest (days)', 'Under 30', '30–59', '60–89', '90+'],
        rows: rows.map((r) => [
          String(r.name ?? ''),
          String(r.kind ?? ''),
          minor(r.owedMinor),
          String(r.oldestDays ?? 0),
          minor(r.current),
          minor(r.d30),
          minor(r.d60),
          minor(r.d90),
        ]),
      };
    },
  },
  {
    key: 'wallet',
    label: 'Wallet movements',
    source: 'top-ups and usage on the messaging wallet, with the balance after each',
    fetch: async (period) => {
      const res = await apiClient.get('/accounts/export/wallet', { params: { period } });
      const rows = list<Record<string, unknown>>(res.data);
      return {
        headers: ['Date', 'Type', 'Resource', 'Units', 'Amount', 'Balance after', 'Currency', 'Reference'],
        rows: rows.map((r) => [
          String(r.date ?? ''),
          String(r.type ?? ''),
          String(r.resource ?? ''),
          String(r.units ?? ''),
          minor(r.deltaMinor),
          minor(r.balanceAfterMinor),
          String(r.currency ?? ''),
          String(r.reference ?? ''),
        ]),
      };
    },
  },
  {
    key: 'dayclose',
    label: 'Day-close reports',
    source: 'each closed day: counted cash against the cash its bills took',
    fetch: async (period) => {
      const res = await apiClient.get('/accounts/export/day-close', { params: { period } });
      const rows = list<Record<string, unknown>>(res.data);
      return {
        headers: ['Date', 'Branch', 'Counted cash', 'Cash billed', 'Closed at', 'Closed by', 'Note'],
        rows: rows.map((r) => [
          String(r.date ?? ''),
          String(r.branch ?? ''),
          r.countedCashMinor == null ? '' : minor(r.countedCashMinor),
          minor(r.cashBilledMinor),
          String(r.closedAt ?? ''),
          String(r.closedBy ?? ''),
          String(r.note ?? ''),
        ]),
      };
    },
  },
];

export function ExportPage() {
  const [period, setPeriod] = useState(thisPeriod);
  const [busy, setBusy] = useState<string | null>(null);

  const { data: summary } = useAccountsSummary(period);
  const { data: log } = useExportLog();
  const record = useLogExport();
  const clinic = useTenantStore((st) => st.publicConfig)?.displayName ?? 'Clinic';
  const [query, setQuery] = useState('');
  const narrow = useNarrowViewport();

  usePageMeta({ title: 'Export center' });

  const pull = async (table: Table, as: 'csv' | 'pdf' | 'xlsx' = 'xlsx') => {
    setBusy(table.key);
    try {
      const { headers, rows } = await table.fetch(period);
      if (!rows.length) {
        toast.error(`${table.label} has nothing in ${monthName(period)}`);
        return;
      }
      if (as === 'xlsx') {
        await exportXlsx(`${table.key}-${period}`, [{ name: table.label, headers, rows }]);
      } else if (as === 'pdf') {
        exportPdf(
          `${table.key}-${period}`,
          clinic,
          `${table.label} — ${monthName(period)} ${period.slice(0, 4)}`,
          headers,
          rows,
        );
      } else {
        exportRows(`${table.key}-${period}`, headers, rows);
      }
      record.mutate({
        tableKey: table.key,
        label: table.label,
        period,
        format: as,
        rows: rows.length,
      });
      toast.success(`${rows.length} rows · logged against your name`);
    } catch {
      toast.error(`${table.label} did not come back. Nothing has been exported.`);
    } finally {
      setBusy(null);
    }
  };

  /* THE CLOSE PACKAGE IS ONE WORKBOOK: every table, a sheet each. A table
     that fails to load is named and left out rather than half-producing the
     file; one that is empty for the month is simply not a sheet. */
  const closePackage = async () => {
    setBusy('close');
    try {
      const sheets: Array<{ name: string; headers: string[]; rows: string[][] }> = [];
      const failed: string[] = [];
      for (const table of TABLES) {
        try {
          const { headers, rows } = await table.fetch(period);
          if (rows.length) sheets.push({ name: table.label, headers, rows });
        } catch {
          failed.push(table.label);
        }
      }
      if (!sheets.length) {
        toast.error(`There is nothing to export for ${monthName(period)}`);
        return;
      }
      await exportXlsx(`close-package-${period}`, sheets);
      const total = sheets.reduce((n, sh) => n + sh.rows.length, 0);
      record.mutate({ tableKey: 'close', label: 'Close package', period, format: 'xlsx', rows: total });
      if (failed.length) {
        toast.warning(`${sheets.length} sheets exported — ${failed.join(', ')} did not come back`);
      } else {
        toast.success(`${sheets.length} sheets · ${total} rows · logged against your name`);
      }
    } finally {
      setBusy(null);
    }
  };

  const crumb = useAccountsCrumb('Accounts', `Export · ${periodLabel(period).split(' ')[0]}`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: this pane is part of the task, so it follows the content. */
      paneInline: true,
      phoneMenu: (close) => <AccountantMenu door="export" onClose={close} />,
      title: 'Export center',
      sub: 'the monthly close package',
      search: 'Search an export, a table…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Close package (.xlsx)',
        disabled: !!busy,
        onClick: () => void closePackage(),
      },
      pane: (
        <Pane
          head={
            <PaneHead overline="This screen" title="Export log" sub="what left the building" />
          }
        >
          {(log ?? []).length ? (
            (log ?? []).slice(0, 12).map((e) => (
              <Card key={e.id}>
                <p className="t-ovl mut">
                  {e.label} · {dayLabel(String(e.createdAt).slice(0, 10))}
                </p>
                <p className="t-sec sec mt4">
                  by {e.pulledBy ?? 'somebody'} · .{e.format}
                  {e.period ? ` · ${monthName(e.period)}` : ''} · {e.rows} rows
                </p>
              </Card>
            ))
          ) : (
            <div className="emptystate">
              <p className="t-sec">
                Nothing has been exported yet. When it is, it appears here — who pulled what, when.
              </p>
            </div>
          )}
          <Card kind="accent">
            <p className="t-sec brand">Who pulled what, when — always.</p>
          </Card>
        </Pane>
      ),
    },
    [log, busy, period, query],
  );

  const rows: TableRow[] = TABLES.filter(
    (t) => !query.trim() || `${t.label} ${t.source}`.toLowerCase().includes(query.trim().toLowerCase()),
  ).map((t) => ({
    id: t.key,
    cells: {
      table: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">{t.label}</p>
          {narrow ? null : <p className="t-cap mut">{t.source}</p>}
        </div>
      ),
      formats: (
        <span className="flex" style={{ gap: 6 }}>
          <DChip onClick={() => void pull(t, 'xlsx')}>{busy === t.key ? 'pulling…' : '.xlsx'}</DChip>
          <DChip onClick={() => void pull(t, 'csv')}>.csv</DChip>
          {narrow ? null : <DChip onClick={() => void pull(t, 'pdf')}>Branded PDF</DChip>}
        </span>
      ),
    },
  }));

  return (
    <div className="maie desk acc-root">
      <PeriodRow period={period} onChange={setPeriod} branches={summary?.branches} />

      <div className="dcontent scroll">
        {narrow ? (
          <Card kind="accent">
            <div className="flex" style={{ alignItems: 'center', gap: 12 }}>
              <div className="grow">
                <p className="t-strong">Monthly close package</p>
                <p className="t-cap mut">everything below, one .xlsx</p>
              </div>
              <Button kind="primary" disabled={!!busy} onClick={() => void closePackage()}>
                Export
              </Button>
            </div>
          </Card>
        ) : null}
        <DataTable
          caption="What can be exported"
          columns={[
            { key: 'table', label: 'Table', width: 62 },
            { key: 'formats', label: 'Formats', width: 38 },
          ]}
          rows={rows}
          empty={<div />}
        />

        {narrow ? (
          <p className="t-cap mut mt12" style={{ textAlign: 'center' }}>
            every export logged · branded PDF also available
          </p>
        ) : (
          <>
        <p className="t-sec mut mt12">
          Every export is logged — who pulled what, when. A branded PDF is the same table on the
          clinic’s letterhead, for anything that leaves the building on paper. The close package is
          period-stamped and reconciled to the day closes; the spend side rides in it too.
        </p>
          </>
        )}
      </div>
    </div>
  );
}
